import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { animated, useSpring } from '@react-spring/web';
import { useWindowWidth } from '@wojtekmaj/react-hooks';
import T from '@wojtekmaj/react-t';
import { startOfMonth } from 'date-fns';
import {
  ArrowLeftRightIcon,
  BadgePercentIcon,
  BoxesIcon,
  BriefcaseIcon,
  ClipboardIcon,
  DollarSignIcon,
  HardDriveIcon,
  HomeIcon,
  LineChartIcon,
  MapPinIcon,
  ShieldAlertIcon,
  UserCogIcon,
  UsersIcon,
} from 'lucide-react';

import {
  Badge,
  Icon,
  Label,
  List,
  ListItem,
  ListItemLink,
  ListItemUnderlay,
  TabIndicator,
  Wrapper,
} from './menu.styles';

import PermissionListItemLink from './permission_list_item_link';

import { formatISODate } from '../../../utils/date';

import { cardState, pendingSecurityEventsCountLoadable } from '../../../store';

import { MODE } from '../../../env';

import type { AccountRolePermissions } from '@rewardopl/types';

const shouldDisplayIngredients = MODE === 'saladstory-test' || MODE === 'saladstory-production';

const AnimatedTabIndicator = animated(TabIndicator);

type Item = {
  label: string;
  icon: React.ComponentType;
  href: string;
  exact?: boolean;
  permission?: keyof AccountRolePermissions;
};

type ItemWithBadge = Item & { badge?: number };

type ItemWithBadgeAndFlags = ItemWithBadge & { active: boolean };

function areRectsEqual(prevRect: DOMRect | null, newRect: DOMRect): boolean {
  return Boolean(prevRect && prevRect.x === newRect.x && prevRect.width === newRect.width);
}

const now = new Date();

const dateFrom = formatISODate(startOfMonth(now));
const dateTo = formatISODate(now);

const params = new URLSearchParams({
  date_from: dateFrom,
  date_to: dateTo,
});

type MenuItemProps = {
  item: ItemWithBadgeAndFlags;
  onActive: (layout: DOMRect, parentLayout: DOMRect) => void;
};

function MenuItem({ item, onActive }: MenuItemProps) {
  const [domRect, setDomRect] = useState<DOMRect | null>(null);
  const [parentDomRect, setParentDomRect] = useState<DOMRect | null>(null);

  const { label, icon: IconComponent, href, exact, permission, badge, active } = item;

  useEffect(() => {
    if (!active || !domRect || !parentDomRect) {
      return;
    }

    onActive(domRect, parentDomRect);
  }, [active, domRect, onActive, parentDomRect]);

  const content = (
    <>
      <ListItemUnderlay />
      <Icon>
        <IconComponent />
      </Icon>
      <Label>
        <T>{label}</T>
      </Label>
      {badge ? <Badge>{badge}</Badge> : null}
    </>
  );

  const link = permission ? (
    <PermissionListItemLink end={exact} permission={permission} to={href}>
      {content}
    </PermissionListItemLink>
  ) : (
    <ListItemLink end={exact} to={href}>
      {content}
    </ListItemLink>
  );

  return (
    <ListItem
      ref={(ref) => {
        if (!ref) {
          return;
        }

        function getRect() {
          if (!ref) {
            return;
          }

          const parentElement = ref.parentElement;

          if (!parentElement) {
            return;
          }

          const rect = ref.getBoundingClientRect();
          const parentRect = parentElement.getBoundingClientRect();

          setDomRect((prevDomRect) => (areRectsEqual(prevDomRect, rect) ? prevDomRect : rect));

          setParentDomRect((prevParentRect) =>
            areRectsEqual(prevParentRect, parentRect) ? prevParentRect : parentRect,
          );
        }

        getRect();

        document.fonts.addEventListener('loadingdone', getRect);
      }}
    >
      {link}
    </ListItem>
  );
}

export default function Menu() {
  const windowWidth = useWindowWidth();
  const location = useLocation();
  const card = useAtomValue(cardState);
  const pendingSecurityEventsCountLoadableValue = useAtomValue(pendingSecurityEventsCountLoadable);
  const shouldUseImmediate = useRef(true);
  const [indicatorRect, setIndicatorRect] = useState<object>({ left: 0 });

  const props = useSpring(indicatorRect);

  const pendingSecurityEventsCount =
    pendingSecurityEventsCountLoadableValue.state === 'hasData'
      ? pendingSecurityEventsCountLoadableValue.data
      : null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect intentionally triggered on window width change
  useEffect(() => {
    shouldUseImmediate.current = true;
  }, [windowWidth]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect intentionally triggered on location change
  useEffect(() => {
    if ('OpenWidget' in window && OpenWidget) {
      OpenWidget.call('minimize');
    }
  }, [location.pathname]);

  const items = useMemo(
    () =>
      [
        {
          label: 'Home',
          icon: HomeIcon,
          href: '/',
          exact: true,
        },
        {
          label: 'Businesses',
          icon: BriefcaseIcon,
          href: '/businesses',
          permission: 'businesses_view',
        },
        card
          ? {
              label: 'Loyalty program',
              icon: BadgePercentIcon,
              href: `/cards/${card._id}`,
              permission: 'cards_view',
            }
          : {
              label: 'Loyalty programs',
              icon: BadgePercentIcon,
              href: '/cards',
              permission: 'cards_view',
            },
        {
          label: 'Transactions',
          icon: ArrowLeftRightIcon,
          href: '/transactions',
          permission: 'transactions_view',
        },
        {
          label: 'Places',
          icon: MapPinIcon,
          href: '/places',
          permission: 'places_view',
        },
        ...(shouldDisplayIngredients
          ? ([
              {
                label: 'Ingredients',
                icon: BoxesIcon,
                href: '/ingredients',
                permission: 'ingredients_view',
              },
            ] satisfies Item[])
          : []),
        {
          label: 'Offer',
          icon: ClipboardIcon,
          href: '/products',
          permission: 'products_view',
        },
        {
          label: 'Account roles',
          icon: UserCogIcon,
          href: '/account_roles',
          permission: 'account_roles_view',
        },
        {
          label: 'Accounts',
          icon: UsersIcon,
          href: '/accounts',
          permission: 'accounts_view',
        },
        {
          label: 'Devices',
          icon: HardDriveIcon,
          href: '/devices',
          permission: 'devices_view',
        },
        {
          label: 'Plans',
          icon: DollarSignIcon,
          href: '/plans',
          permission: 'plans_view',
        },
        {
          label: 'Security events',
          icon: ShieldAlertIcon,
          href: '/security_events',
          permission: 'security_events_view',
        },
        {
          label: 'Users',
          icon: UsersIcon,
          href: '/users',
          permission: 'users_view',
        },
        {
          label: 'Stats',
          icon: LineChartIcon,
          href: `/stats?${params}`,
          permission: 'stats_view',
        },
      ] satisfies Item[],
    [card],
  );

  const itemsWithBadge = useMemo<ItemWithBadge[]>(
    () =>
      pendingSecurityEventsCount
        ? items.map((item) =>
            item.href === '/security_events'
              ? { ...item, badge: pendingSecurityEventsCount }
              : item,
          )
        : items,
    [items, pendingSecurityEventsCount],
  );

  const itemsWithBadgeAndFlags = useMemo<ItemWithBadgeAndFlags[]>(
    () =>
      itemsWithBadge.map((item) => {
        const pattern = item.exact ? item.href : `${item.href}/*`;
        const match = matchPath({ path: pattern, end: item.exact }, location.pathname);

        return { ...item, active: Boolean(match) };
      }),
    [itemsWithBadge, location.pathname],
  );

  const activeItem = itemsWithBadgeAndFlags.find((item) => item.active);
  const hasActiveItem = Boolean(activeItem);

  useEffect(() => {
    if (hasActiveItem) {
      return;
    }

    setIndicatorRect({
      opacity: 0,
      immediate: true,
    });

    shouldUseImmediate.current = true;
  }, [hasActiveItem]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect intentionally triggered on window width change
  useEffect(() => {
    shouldUseImmediate.current = true;
  }, [windowWidth]);

  const onActive = useCallback((rect: DOMRect, parentRect: DOMRect) => {
    const immediate = shouldUseImmediate.current;

    setIndicatorRect({
      left: rect.x - parentRect.x + rect.width / 2,
      opacity: 1,
      immediate,
    });

    shouldUseImmediate.current = false;
  }, []);

  return (
    <Wrapper>
      <List role="list">
        {itemsWithBadgeAndFlags.map((item) => (
          <MenuItem item={item} key={item.href} onActive={onActive} />
        ))}
      </List>
      <AnimatedTabIndicator style={props} />
    </Wrapper>
  );
}
